import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CitaDetalle = ({ cita, onActualizarEstado, onActualizarPago }) => {
  const [estadoActual, setEstadoActual] = useState(cita?.estado || '');
  const [estadoPagoActual, setEstadoPagoActual] = useState(cita?.pago?.estado || '');
  const [mostrarComprobante, setMostrarComprobante] = useState(false);
  
  // Si la cita o pago no existen, mostrar un mensaje
  if (!cita) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
        No se pudo cargar la información de la cita
      </div>
    );
  }
  
  // Función para cambiar el estado de la cita
  const cambiarEstado = (nuevoEstado) => {
    if (onActualizarEstado) {
      onActualizarEstado(nuevoEstado);
    }
    setEstadoActual(nuevoEstado);
  };
  
  // Función para cambiar el estado del pago
  const cambiarEstadoPago = (nuevoEstado) => {
    if (onActualizarPago) {
      onActualizarPago(nuevoEstado);
    }
    setEstadoPagoActual(nuevoEstado);
  };
  
  // Estilos para el estado
  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'confirmada': return { bg: '#e8f5e9', color: '#4caf50' };
      case 'pendiente': return { bg: '#fff3e0', color: '#ff9800' };
      case 'completada': return { bg: '#e1f5fe', color: '#03a9f4' };
      case 'cancelada': return { bg: '#ffebee', color: '#f44336' };
      default: return { bg: '#f5f5f5', color: '#757575' };
    }
  };
  
  // Estilos para el estado de pago
  const getPagoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'completado': return { bg: '#e8f5e9', color: '#4caf50' };
      case 'pendiente': return { bg: '#fff3e0', color: '#ff9800' };
      case 'reembolsado': return { bg: '#ffebee', color: '#f44336' };
      default: return { bg: '#f5f5f5', color: '#757575' };
    }
  };
  
  const estadoColor = getEstadoColor(estadoActual);
  const pagoColor = getPagoColor(estadoPagoActual);
  
  // Comprobar si se puede generar comprobante
  const puedeGenerarComprobante = cita.pago?.metodo === 'TARJETA' && estadoPagoActual?.toLowerCase() === 'completado';
  
  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    
    const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
    return fechaObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Formatear fecha y hora
  const formatearFechaHora = (fecha) => {
    if (!fecha) return '';
    
    const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
    return fechaObj.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumeroCita = (numero) => {
    if (typeof numero !== 'number') return null;
    return `C-${String(numero).padStart(4, '0')}`;
  };

  const etiquetaCita = formatNumeroCita(cita.numero) || cita.id;
  
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      {/* Encabezado con ID y estado */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0' }}>Cita #{etiquetaCita}</h3>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
            Creada el {formatearFecha(cita.createdAt || cita.fecha)}
          </div>
        </div>
        
        <div>
          <span style={{
            padding: '8px 15px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 'bold',
            backgroundColor: estadoColor.bg,
            color: estadoColor.color,
            marginRight: '10px'
          }}>
            {estadoActual ? estadoActual.charAt(0).toUpperCase() + estadoActual.slice(1).toLowerCase() : 'Desconocido'}
          </span>
          
          {cita.pago && (
            <span style={{
              padding: '8px 15px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: 'bold',
              backgroundColor: pagoColor.bg,
              color: pagoColor.color
            }}>
              Pago {estadoPagoActual ? estadoPagoActual.charAt(0).toUpperCase() + estadoPagoActual.slice(1).toLowerCase() : 'Desconocido'}
            </span>
          )}
        </div>
      </div>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        {/* Información del Paciente */}
        <div style={{ flex: '1 1 300px' }}>
          <div style={{ 
            backgroundColor: '#f9f9f9', 
            padding: '15px', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 15px 0' }}>Información del Paciente</h4>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Nombre:</div>
              <div style={{ fontWeight: 'bold' }}>
                {cita.paciente ? `${cita.paciente.nombre} ${cita.paciente.apellido}` : 'No disponible'}
              </div>
            </div>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Cédula:</div>
              <div>{cita.paciente?.cedula || 'No disponible'}</div>
            </div>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Email:</div>
              <div>{cita.paciente?.email || 'No disponible'}</div>
            </div>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Teléfono:</div>
              <div>{cita.paciente?.telefono || 'No disponible'}</div>
            </div>
            
            {/* Botón para ver historia clínica */}
            {cita.paciente?.cedula && (
              <div style={{ marginTop: '15px' }}>
                <Link to={`/dashboard/historia-clinica/${cita.paciente.cedula}`} style={{
                  display: 'inline-block',
                  padding: '8px 15px',
                  backgroundColor: '#3f51b5',
                  color: 'white',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  <i className="fas fa-file-medical" style={{ marginRight: '5px' }}></i>
                  Ver Historia Clínica
                </Link>
              </div>
            )}
          </div>
          
          {/* Información de la Cita */}
          <div style={{ 
            backgroundColor: '#f9f9f9', 
            padding: '15px', 
            borderRadius: '8px'
          }}>
            <h4 style={{ margin: '0 0 15px 0' }}>Detalles de la Cita</h4>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Servicio:</div>
              <div style={{ fontWeight: 'bold' }}>{cita.servicio?.nombre || 'No disponible'}</div>
            </div>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Fecha:</div>
              <div>
                {formatearFechaHora(cita.fecha)}
              </div>
            </div>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Hora:</div>
              <div>
                {cita.horaInicio || ''} - {cita.horaFin || ''}
              </div>
            </div>
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <div style={{ width: '100px', color: '#666' }}>Precio:</div>
              <div>${cita.servicio?.precio || 'No disponible'}</div>
            </div>
            {cita.motivoConsulta && (
              <div style={{ display: 'flex', marginBottom: '10px' }}>
                <div style={{ width: '100px', color: '#666' }}>Motivo:</div>
                <div>{cita.motivoConsulta}</div>
              </div>
            )}
            {cita.notas && (
              <div style={{ display: 'flex', marginBottom: '10px' }}>
                <div style={{ width: '100px', color: '#666' }}>Notas:</div>
                <div>{cita.notas}</div>
              </div>
            )}
          </div>
        </div>
        
        {/* Información de Pago y Acciones */}
        <div style={{ flex: '1 1 300px' }}>
          {cita.pago && (
            <div style={{ 
              backgroundColor: '#f9f9f9', 
              padding: '15px', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 15px 0' }}>Información de Pago</h4>
              <div style={{ display: 'flex', marginBottom: '10px' }}>
                <div style={{ width: '120px', color: '#666' }}>Método:</div>
                <div>
                  {cita.pago.metodo === 'TARJETA' ? 'Tarjeta de crédito/débito' : 
                   cita.pago.metodo === 'TRANSFERENCIA' ? 'Transferencia bancaria' : 'Efectivo'}
                </div>
              </div>
              <div style={{ display: 'flex', marginBottom: '10px' }}>
                <div style={{ width: '120px', color: '#666' }}>Estado:</div>
                <div>
                  <span style={{
                    padding: '3px 8px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    backgroundColor: pagoColor.bg,
                    color: pagoColor.color
                  }}>
                    {estadoPagoActual ? estadoPagoActual.charAt(0).toUpperCase() + estadoPagoActual.slice(1).toLowerCase() : 'Desconocido'}
                  </span>
                </div>
              </div>
              {cita.pago.referencia && (
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <div style={{ width: '120px', color: '#666' }}>Referencia:</div>
                  <div>{cita.pago.referencia}</div>
                </div>
              )}
              {cita.pago.fechaPago && (
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <div style={{ width: '120px', color: '#666' }}>Fecha de pago:</div>
                  <div>
                    {formatearFecha(cita.pago.fechaPago)}
                  </div>
                </div>
              )}
              
              {/* Visualización de comprobante de pago */}
              {cita.pago.comprobante && (
                <div style={{ marginTop: '15px' }}>
                  <button 
                    onClick={() => setMostrarComprobante(!mostrarComprobante)}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    <i className={`fas fa-${mostrarComprobante ? 'eye-slash' : 'eye'}`} style={{ marginRight: '5px' }}></i>
                    {mostrarComprobante ? 'Ocultar comprobante' : 'Ver comprobante'}
                  </button>
                  
                  {mostrarComprobante && (
                    <div style={{ marginTop: '15px' }}>
                      <img 
                        src={cita.pago.comprobante} 
                        alt="Comprobante de pago" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '300px', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px' 
                        }} 
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Generar comprobante si es tarjeta y pago completado */}
              {puedeGenerarComprobante && (
                <div style={{ marginTop: '15px' }}>
                  <Link to={`/dashboard/comprobante/${cita.id}`} style={{
                    display: 'inline-block',
                    padding: '8px 15px',
                    backgroundColor: '#3498db',
                    color: 'white',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    <i className="fas fa-file-invoice" style={{ marginRight: '5px' }}></i>
                    Ver Comprobante
                  </Link>
                </div>
              )}
            </div>
          )}
          
          {/* Acciones */}
          <div style={{ 
            backgroundColor: '#f9f9f9', 
            padding: '15px', 
            borderRadius: '8px'
          }}>
            <h4 style={{ margin: '0 0 15px 0' }}>Acciones</h4>
            
            <div style={{ marginBottom: '15px' }}>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>Cambiar estado de la cita:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <button 
                  onClick={() => cambiarEstado('PENDIENTE')}
                  style={{
                    padding: '8px 15px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: estadoActual?.toUpperCase() === 'PENDIENTE' ? '#ff9800' : '#f5f5f5',
                    color: estadoActual?.toUpperCase() === 'PENDIENTE' ? 'white' : '#666',
                    cursor: 'pointer'
                  }}
                >
                  Pendiente
                </button>
                
                <button 
                  onClick={() => cambiarEstado('CONFIRMADA')}
                  style={{
                    padding: '8px 15px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: estadoActual?.toUpperCase() === 'CONFIRMADA' ? '#4caf50' : '#f5f5f5',
                    color: estadoActual?.toUpperCase() === 'CONFIRMADA' ? 'white' : '#666',
                    cursor: 'pointer'
                  }}
                >
                  Confirmada
                </button>
                
                <button 
                  onClick={() => cambiarEstado('COMPLETADA')}
                  style={{
                    padding: '8px 15px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: estadoActual?.toUpperCase() === 'COMPLETADA' ? '#03a9f4' : '#f5f5f5',
                    color: estadoActual?.toUpperCase() === 'COMPLETADA' ? 'white' : '#666',
                    cursor: 'pointer'
                  }}
                >
                  Completada
                </button>
                
                <button 
                  onClick={() => cambiarEstado('CANCELADA')}
                  style={{
                    padding: '8px 15px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: estadoActual?.toUpperCase() === 'CANCELADA' ? '#f44336' : '#f5f5f5',
                    color: estadoActual?.toUpperCase() === 'CANCELADA' ? 'white' : '#666',
                    cursor: 'pointer'
                  }}
                >
                  Cancelada
                </button>
              </div>
            </div>
            
            {cita.pago && (
              <div>
                <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>Cambiar estado del pago:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  <button 
                    onClick={() => cambiarEstadoPago('PENDIENTE')}
                    style={{
                      padding: '8px 15px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: estadoPagoActual?.toUpperCase() === 'PENDIENTE' ? '#ff9800' : '#f5f5f5',
                      color: estadoPagoActual?.toUpperCase() === 'PENDIENTE' ? 'white' : '#666',
                      cursor: 'pointer'
                    }}
                  >
                    Pendiente
                  </button>
                  
                  <button 
                    onClick={() => cambiarEstadoPago('COMPLETADO')}
                    style={{
                      padding: '8px 15px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: estadoPagoActual?.toUpperCase() === 'COMPLETADO' ? '#4caf50' : '#f5f5f5',
                      color: estadoPagoActual?.toUpperCase() === 'COMPLETADO' ? 'white' : '#666',
                      cursor: 'pointer'
                    }}
                  >
                    Completado
                  </button>
                  
                  <button 
                    onClick={() => cambiarEstadoPago('REEMBOLSADO')}
                    style={{
                      padding: '8px 15px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: estadoPagoActual?.toUpperCase() === 'REEMBOLSADO' ? '#f44336' : '#f5f5f5',
                      color: estadoPagoActual?.toUpperCase() === 'REEMBOLSADO' ? 'white' : '#666',
                      cursor: 'pointer'
                    }}
                  >
                    Reembolsado
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitaDetalle;